import { logReportDownload } from '../../util/auditLogger';
import React, { useState, useCallback } from "react";
import { useToast } from "../../components/ui";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components";
import { Header } from "../../components/modules";
import { FiFileText } from "react-icons/fi";
import { MdPersonSearch } from "react-icons/md";
import { Spinner } from "reactstrap";
import * as xlsx from "xlsx";
import Context from "../../Context";
import { statuses } from "../../constants";
import { thaiDate } from "../../util";
import dayjs from "dayjs";
import buddhistEra from "dayjs/plugin/buddhistEra";
import dayTh from "dayjs/locale/th";
import { toast } from "sonner";

dayjs.extend(buddhistEra);
dayjs.locale(dayTh);

// ============================================
// Constants
// ============================================
const TICKET_PRICE = 80;
const TAX_DIVISOR = 100 / 107;
const QUERY_LIMIT = 5000;
const ADMIN_USER_ID = "201989";

const CSV_HEADERS = [
  "ลำดับ",
  "เลขที่ใบกำกับภาษี",
  "วันที่อนุมัติ",
  "เวลาที่อนุมัติ",
  "รหัสคำสั่งซื้อ",
  "รหัสผู้ใช้",
  "ชื่อ-นามสกุล",
  "คูปอง",
  "มูลค่าสลาก",
  "ค่าบริการ",
  "มูลค่าก่อนภาษี",
  "ภาษี 7%",
  "ราคารวม",
];

// ============================================
// Helper Functions
// ============================================
const formatThaiDate = (date) => {
  const d = new Date(date);
  return {
    date: d.toLocaleDateString("th-TH", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
    }),
    time: d.toLocaleTimeString("th-TH", {
      hour: "numeric",
      minute: "numeric",
    }),
  };
};

const isPointRedeemedCoupon = (coupon) => coupon?.isPointRedeem;
const isVoucherType = (coupon) =>
  coupon?.type === "voucher" ||
  coupon?.type === "voucher-lock";

const getQuantity = (coupon) =>
  isPointRedeemedCoupon(coupon) || !isVoucherType(coupon)
    ? "-"
    : coupon?.quantity?.toLocaleString();

const getTicketPrice = (coupon) => {
  if (isPointRedeemedCoupon(coupon) || !isVoucherType(coupon)) return "-";
  return Math.max(0, (coupon?.quantity || 0) * TICKET_PRICE)?.toLocaleString();
};

const calculatePricing = (coupon, price) => {
  if (isPointRedeemedCoupon(coupon)) {
    return {
      serviceFee: "-",
      preTaxValue: "-",
      tax: "-",
      totalPrice: "-",
    };
  }

  const rawTicketPriceVal = isVoucherType(coupon)
    ? Math.min(price, (coupon?.quantity || 0) * TICKET_PRICE)
    : 0;

  const serviceValue = isVoucherType(coupon)
    ? Math.max(0, price - rawTicketPriceVal)
    : Math.max(0, price);

  const preTaxValue = serviceValue * TAX_DIVISOR;
  const tax = serviceValue * (1 - TAX_DIVISOR);

  return {
    ticketPrice: isVoucherType(coupon) ? (rawTicketPriceVal > 0 ? rawTicketPriceVal.toLocaleString() : "0") : "-",
    serviceFee: serviceValue.toFixed(2).toLocaleString(),
    preTaxValue: preTaxValue.toFixed(2).toLocaleString(),
    tax: tax.toFixed(2).toLocaleString(),
    totalPrice: Math.max(0, price).toLocaleString(),
  };
};

const getBundleVoucherQuantity = (bundle) => {
  if (!bundle) return 0;

  // 1. Attempt to parse ticket count from the name (e.g., "สลาก 50 ใบ")
  const bundleName = bundle.name || "";
  const match = bundleName.match(/สลาก\s*(\d+)\s*ใบ/);
  if (match) {
    return parseInt(match[1], 10);
  }

  // 2. Fallback to original item quantity sum if no name match
  if (!bundle.items) return 0;
  return bundle.items.reduce((sum, item) => {
    if (item.type === "voucher" || item.type === "voucher-lock") {
      return sum + (item.quantity || 0);
    }
    return sum;
  }, 0);
};

const calculateBundlePricing = (bundle, price) => {
  if (bundle?.isPointRedeem) {
    return {
      ticketPrice: "-",
      serviceFee: "-",
      preTaxValue: "-",
      tax: "-",
      totalPrice: "-",
    };
  }

  const voucherQty = getBundleVoucherQuantity(bundle);
  const ticketValue = Math.min(price, voucherQty * TICKET_PRICE);
  const serviceValue = Math.max(0, price - ticketValue);

  const preTaxValue = serviceValue * TAX_DIVISOR;
  const tax = serviceValue * (1 - TAX_DIVISOR);

  return {
    ticketPrice: voucherQty > 0 ? (ticketValue > 0 ? ticketValue.toLocaleString() : "0") : "-",
    serviceFee: serviceValue.toFixed(2).toLocaleString(),
    preTaxValue: preTaxValue.toFixed(2).toLocaleString(),
    tax: tax.toFixed(2).toLocaleString(),
    totalPrice: Math.max(0, price).toLocaleString(),
  };
};

// ============================================
// Subcomponent: Tax Coupon Search Panel
// ============================================
const TaxCouponSearchPanel = ({
  configs,
  selectedRoundDate,
  loading,
  onRoundDateChange,
  onSearch,
  onDownload,
  onInjectTaxNo,
  isAdmin,
}) => (
  <div className="relative w-96 bg-white rounded-lg shadow-md mt-4 border border-gray-300 overflow-hidden">
    <div className="p-3 border-b border-gray-300">
      <p className="font-semibold">รายงานภาษี - คูปอง</p>
    </div>
    <div className="relative grid grid-cols-2 items-center gap-3 p-3">
      {loading && (
        <div className="z-50 bg-[rgb(255,255,255,0.7)] absolute h-full w-full flex justify-center items-center">
          <Spinner className="w-4 h-4" />
        </div>
      )}
      <div className="gap-2 col-span-2">
        <p className="font-medium text-xs">งวด</p>
        <select
          className="border-2 w-full h-9 border-gray-300 rounded-lg cursor-pointer disabled:cursor-not-allowed disabled:opacity-65"
          value={selectedRoundDate}
          onChange={onRoundDateChange}
          disabled={loading}
        >
          {configs.roundDates.map((roundDate) => (
            <option key={roundDate} value={roundDate}>
              {roundDate}
            </option>
          ))}
        </select>
      </div>

      <button
        disabled={loading}
        className="flex items-center justify-center gap-1 py-2 px-3 bg-blue text-white text-sm font-semibold rounded-lg shadow-md hover:opacity-60 transition-all duration-200 disabled:opacity-60"
        onClick={onSearch}
        aria-label="ค้นหาคำสั่งซื้อ"
      >
        <MdPersonSearch className="text-white" />
        ค้นหา
      </button>

      <button
        disabled={loading}
        className="flex items-center justify-center gap-1 py-2 px-3 bg-[rgb(59,176,101)] text-white text-sm font-semibold rounded-lg shadow-md hover:opacity-60 transition-all duration-200 disabled:bg-slate-300 disabled:hover:opacity-100 disabled:cursor-not-allowed"
        onClick={onDownload}
        aria-label="ดาวโหลดรายงานภาษี"
      >
        {loading ? (
          <Spinner className="w-3 h-3" />
        ) : (
          <MdPersonSearch className="text-white" />
        )}
        ดาวโหลด
      </button>

      {isAdmin && (
        <button
          disabled={!isAdmin}
          className="flex items-center justify-center gap-1 py-2 px-3 bg-red-600 text-white text-sm font-semibold rounded-lg shadow-md hover:opacity-60 transition-all duration-200 disabled:bg-slate-300 disabled:hover:opacity-100 disabled:cursor-not-allowed"
          onClick={onInjectTaxNo}
          aria-label="เพิ่มหมายเลขใบกำกับภาษี"
        >
          Inject Tax No
        </button>
      )}
    </div>
  </div>
);

export const ReportTaxCoupon = () => {
  const { callApi, configs, profileState } = Context();

  // State management
  const [orders, setOrders] = useState(false);
  const [selectedRoundDate, setSelectedRoundDate] = useState(
    configs?.roundDate || "",
  );
  const [loading, setLoading] = useState(false);

  // ============================================
  // API Handlers
  // ============================================
  const getOrders = useCallback(async () => {
    try {
      setOrders(false);
      setLoading(true);

      const [couponResult, bundleResult] = await Promise.all([
        callApi(
          "coupons-getAllCouponOrders",
          {
            queries: [["status", statuses.APPROVE],],
            limit: QUERY_LIMIT,
            roundDate: selectedRoundDate,
            orderBy: ["createdAt", "asc"],
          },
          { notShowLoading: true },
        ),
        callApi(
          "coupons-getAllBundleOrders",
          {
            queries: [["status", statuses.APPROVE]],
            limit: QUERY_LIMIT,
            roundDate: selectedRoundDate,
            orderBy: ["createdAt", "asc"],
          },
          { notShowLoading: true },
        ),
      ]);

      const coupons = Array.isArray(couponResult) ? couponResult : [];
      const bundles = Array.isArray(bundleResult) ? bundleResult : [];

      // Filter out coupon orders that belong to a bundle
      const filteredCoupons = coupons.filter((c) => !c.bundleOrderId && c.price > 0);

      // Tag type and combine
      const taggedCoupons = filteredCoupons.map((c) => ({ ...c, _type: "coupon" }));
      const taggedBundles = bundles.map((b) => ({ ...b, _type: "bundle" }));

      const combined = [...taggedCoupons, ...taggedBundles].sort(
        (a, b) => a.createdAt - b.createdAt,
      );

      setOrders(combined);
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  }, [selectedRoundDate, callApi, toast]);

  const handleDownload = useCallback(async () => {
    try {
      if (!orders || orders.length === 0) {
        toast.warning("No orders to download");
        return;
      }

      const data = [CSV_HEADERS];

      orders.forEach((order, index) => {
        const isBundle = order._type === "bundle";
        const coupon = order.coupon;
        const price = order.price;

        let isPointRedeem = false;
        let couponName = "";
        let rawTicketPrice = "-";
        let rawServiceFee = "-";
        let rawPreTaxValue = "-";
        let rawTax = "-";
        let rawTotalPrice = "-";

        if (isBundle) {
          isPointRedeem = order.bundle?.isPointRedeem;
          couponName = `[แพ็กเกจ] ${order.bundle?.name || ""}`;

          if (!isPointRedeem) {
            const voucherQty = getBundleVoucherQuantity(order.bundle);
            const ticketValue = Math.min(price, voucherQty * TICKET_PRICE);
            const serviceValue = Math.max(0, price - ticketValue);

            rawServiceFee = parseFloat(serviceValue.toFixed(2));
            rawPreTaxValue = parseFloat((serviceValue * TAX_DIVISOR).toFixed(2));
            rawTax = parseFloat((serviceValue * (1 - TAX_DIVISOR)).toFixed(2));
            rawTotalPrice = Math.max(0, price);
            if (voucherQty > 0) {
              rawTicketPrice = ticketValue;
            }
          }
        } else {
          isPointRedeem = isPointRedeemedCoupon(coupon);
          couponName = `${coupon?.name || ""} #${coupon?.shortId || ""}`;
          const isVoucher = isVoucherType(coupon);

          if (!isPointRedeem) {
            const ticketValue = isVoucher
              ? Math.min(price, (coupon?.quantity || 0) * TICKET_PRICE)
              : 0;
            const serviceValue = isVoucher
              ? Math.max(0, price - ticketValue)
              : Math.max(0, price);
            rawServiceFee = parseFloat(serviceValue.toFixed(2));
            rawPreTaxValue = parseFloat((serviceValue * TAX_DIVISOR).toFixed(2));
            rawTax = parseFloat((serviceValue * (1 - TAX_DIVISOR)).toFixed(2));
            rawTotalPrice = Math.max(0, price);
            if (isVoucher) {
              rawTicketPrice = ticketValue;
            }
          }
        }

        const { date, time } = formatThaiDate(
          order.approvedAt || order.createdAt,
        );
        data.push([
          index + 1,
          order.taxNo || "-",
          date,
          time,
          order.shortId,
          order.userId,
          order.userData?.name || "-",
          couponName,
          rawTicketPrice,
          rawServiceFee,
          rawPreTaxValue,
          rawTax,
          rawTotalPrice,
        ]);
      });

      const ws = xlsx.utils.aoa_to_sheet(data);
      const wb = xlsx.utils.book_new();
      xlsx.utils.book_append_sheet(wb, ws, "Sheet1");
      logReportDownload();
      xlsx.writeFile(
        wb,
        `รายงานใบกำกับภาษีคูปอง_งวด_${selectedRoundDate}_ออก_ณ_วันที่${thaiDate()}.xlsx`,
      );

      toast.success("Report generated successfully");
    } catch (error) {
      console.error("Error preparing download:", error);
      toast.error("Failed to prepare download");
    }
  }, [orders, selectedRoundDate, toast]);

  const injectTaxNo = useCallback(async () => {
    try {
      if (!orders || orders.length === 0) {
        toast.warning("No orders to inject tax numbers");
        return;
      }

      let count = 1;
      for (const order of orders) {
        const isBundle = order._type === "bundle";
        const isPointRedeem = isBundle
          ? order.bundle?.isPointRedeem
          : isPointRedeemedCoupon(order.coupon);

        if (order.price > 0 && !isPointRedeem) {
          console.log(count, 'order', order)
          const dateStr = order.createdAtDate || new Date(order.createdAt + 7 * 60 * 60 * 1000).toISOString().slice(0, 10);
          const taxNo = `REC${dateStr
            .slice(0, 10)
            .split("-")
            .join("")}${count.toString().padStart(6, "0")}`;

          if (isBundle) {
            await callApi("coupons-editBundleOrder", {
              id: order.id,
              key: "taxNo",
              value: taxNo,
            });
          } else {
            await callApi("coupons-editCouponOrder", {
              id: order.id,
              key: "taxNo",
              value: taxNo,
            });
          }

          count++;
        }
      }

      toast.success(`Injected ${count - 1} tax numbers`);
      await getOrders(); // Refresh orders
    } catch (error) {
      console.error("Error injecting tax numbers:", error);
      toast.error("Failed to inject tax numbers");
    }
  }, [orders, callApi, toast, getOrders]);

  const isAdmin = profileState?.userId === ADMIN_USER_ID;

  return (
    <div className="h-fit w-full max-w-[1600px] mb-24">
      {/* Header */}
      <Header
        header="Report"
        subHeader="รายงานภาษี - คูปอง"
        icon={<FiFileText className="text-blue" />}
      />

      {/* Search Panel */}
      <TaxCouponSearchPanel
        configs={configs}
        selectedRoundDate={selectedRoundDate}
        loading={loading}
        onRoundDateChange={(e) => setSelectedRoundDate(e.target.value)}
        onSearch={getOrders}
        onDownload={handleDownload}
        onInjectTaxNo={injectTaxNo}
        isAdmin={isAdmin}
      />

      {/* Results Table */}
      {orders && <ResultsTable orders={orders} />}
    </div>
  );
};

// ============================================
// Subcomponent: Results Table
// ============================================
const ResultsTable = ({ orders }) => {
  if (!orders || orders.length === 0) {
    return (
      <div className="relative bg-white w-full rounded-lg shadow-md mt-4 border border-gray-300 overflow-hidden">
        <div className="flex bg-white gap-3 items-center p-3 border-b border-gray-300">
          <p className="font-semibold">คำสั่งซื้อทั้งหมด</p>
          <p className="text-xs font-semibold text-[#969797]">0 คำสั่งซื้อ</p>
        </div>
        <div className="min-h-52 w-full flex justify-center items-center font-medium text-[#969797]">
          <div className="text-center">
            <MdPersonSearch className="text-white mx-auto mb-2" />
            ไม่พบคำสั่งซื้อ
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative bg-white w-full rounded-lg shadow-md mt-4 border border-gray-300 overflow-hidden">
      <div className="flex bg-white gap-3 items-center p-3 border-b border-gray-300">
        <p className="font-semibold">คำสั่งซื้อทั้งหมด</p>
        <p className="text-xs font-semibold text-[#969797]">
          {orders.length.toLocaleString()} คำสั่งซื้อ
        </p>
      </div>

      <Table>
        <TableHeader className="bg-[#f2f3f5]">
          <TableRow>
            <TableHead>ลำดับที่</TableHead>
            <TableHead>เลขที่ใบกำกับภาษี</TableHead>
            <TableHead>วันที่อนุมัติ</TableHead>
            <TableHead>เวลาที่อนุมัติ</TableHead>
            <TableHead>รหัสคำสั่งซื้อ</TableHead>
            <TableHead className="text-right">รหัสผู้ใช้</TableHead>
            <TableHead>ชื่อ-นามสกุล</TableHead>
            <TableHead className="text-center">คูปอง</TableHead>
            <TableHead className="text-right">มูลค่าสลาก</TableHead>
            <TableHead className="text-right">ค่าบริการ</TableHead>
            <TableHead className="text-right">มูลค่าก่อนภาษี</TableHead>
            <TableHead className="text-right">ภาษี 7%</TableHead>
            <TableHead className="text-right">ราคารวม</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {orders.map((order, index) => (
            <OrderRow
              key={order.id || order.shortId}
              order={order}
              index={index}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

// ============================================
// Subcomponent: Order Row
// ============================================
const OrderRow = ({ order, index }) => {
  const isBundle = order._type === "bundle";
  const pricing = isBundle
    ? calculateBundlePricing(order.bundle, order.price)
    : calculatePricing(order.coupon, order.price);

  const couponNameText = isBundle
    ? `[แพ็กเกจ] ${order.bundle?.name || ""}`
    : `${order.coupon?.name || ""} #${order.coupon?.shortId || ""}`;

  return (
    <TableRow>
      <TableCell className="font-semibold font-mono tabular-nums text-center">
        {index + 1}
      </TableCell>
      <TableCell className="font-semibold font-mono tabular-nums text-center">
        {order.taxNo || "-"}
      </TableCell>
      <TableCell className="font-medium">
        {formatThaiDate(order.approvedAt || order.createdAt).date}
      </TableCell>
      <TableCell className="font-medium">
        {formatThaiDate(order.approvedAt || order.createdAt).time} น.
      </TableCell>
      <TableCell className="font-semibold font-mono tabular-nums text-center">
        {order.shortId}
      </TableCell>
      <TableCell className="font-semibold text-right font-mono tabular-nums">
        {order.userId}
      </TableCell>
      <TableCell className="font-medium">
        {order.userData?.name || "-"}
      </TableCell>
      <TableCell className="text-center font-medium">
        {couponNameText}
      </TableCell>
      <TableCell className="font-semibold text-right font-mono tabular-nums">
        {pricing.ticketPrice}
      </TableCell>
      <TableCell className="font-semibold text-right font-mono tabular-nums">
        {pricing.serviceFee}
      </TableCell>
      <TableCell className="font-semibold text-right font-mono tabular-nums">
        {pricing.preTaxValue}
      </TableCell>
      <TableCell className="font-semibold text-right font-mono tabular-nums">
        {pricing.tax}
      </TableCell>
      <TableCell className="font-semibold text-right font-mono tabular-nums">
        {pricing.totalPrice}
      </TableCell>
    </TableRow>
  );
};
